/**
 * Writer-friendly prose diff: paragraph structure first, then word-level
 * marks inside changed paragraphs (GitHub-style intent without line chrome).
 */

import { atIndexMaybe, isJust, nothing } from "./maybe";
import { plainProseForHistory } from "./zwdoc/review";

export type DiffMark = {
  type: "equal" | "insert" | "delete";
  text: string;
};

export type DiffBlock =
  { kind: "unchanged"; text: string } | { kind: "changed"; marks: DiffMark[] };

const WORD_TOKEN_RE = /\S+\s*|\s+/g;
const PARA_SPLIT_RE = /\n{2,}/;
/** Cap token DP so huge chapters don't freeze the UI. */
const MAX_WORD_TOKENS = 3500;

/**
 * History text: the `.zwdoc` plain-text projection, with pre-conversion
 * Markdown versions converted first (#252).
 */
export function proseForHistory(doc: string): string {
  return plainProseForHistory(doc);
}

function tokenizeWords(text: string): string[] {
  return text.match(WORD_TOKEN_RE) ?? [];
}

/** Classic LCS backtrace over tokens → equal / insert / delete marks. */
function diffTokens(before: string[], after: string[]): DiffMark[] {
  const n = before.length;
  const m = after.length;
  if (n === 0 && m === 0) return [];
  if (n === 0) {
    return after.map((text) => ({ type: "insert" as const, text }));
  }
  if (m === 0) {
    return before.map((text) => ({ type: "delete" as const, text }));
  }

  const dp: Uint16Array[] = Array.from(
    { length: n + 1 },
    () => new Uint16Array(m + 1),
  );
  for (let i = 1; i <= n; i++) {
    const row = dp[i]!;
    const prevRow = dp[i - 1]!;
    for (let j = 1; j <= m; j++) {
      row[j] =
        before[i - 1] === after[j - 1]
          ? (((prevRow[j - 1] ?? 0) + 1) as number)
          : Math.max(prevRow[j] ?? 0, row[j - 1] ?? 0);
    }
  }

  const raw: DiffMark[] = [];
  let i = n;
  let j = m;
  while (i > 0 || j > 0) {
    const beforeTokM = i > 0 ? atIndexMaybe(before, i - 1) : nothing;
    const afterTokM = j > 0 ? atIndexMaybe(after, j - 1) : nothing;
    const rowM = atIndexMaybe(dp, i);
    const prevRowM = atIndexMaybe(dp, i - 1);
    if (
      i > 0 &&
      j > 0 &&
      isJust(beforeTokM) &&
      isJust(afterTokM) &&
      beforeTokM.value === afterTokM.value
    ) {
      raw.push({ type: "equal", text: beforeTokM.value });
      i--;
      j--;
    } else if (
      j > 0 &&
      isJust(afterTokM) &&
      // Nothing left on the left (i === 0) means the rest is inserted; dp
      // has no row -1 to compare against.
      (i === 0 ||
        (isJust(rowM) &&
          isJust(prevRowM) &&
          (rowM.value[j - 1] ?? 0) >= (prevRowM.value[j] ?? 0)))
    ) {
      raw.push({ type: "insert", text: afterTokM.value });
      j--;
    } else if (isJust(beforeTokM)) {
      raw.push({ type: "delete", text: beforeTokM.value });
      i--;
    } else {
      break;
    }
  }
  raw.reverse();
  return coalesceMarks(raw);
}

function coalesceMarks(marks: DiffMark[]): DiffMark[] {
  const out: DiffMark[] = [];
  for (const mark of marks) {
    const lastM = atIndexMaybe(out, out.length - 1);
    if (isJust(lastM) && lastM.value.type === mark.type) {
      lastM.value.text += mark.text;
    } else {
      out.push({ ...mark });
    }
  }
  return out;
}

function wordDiff(before: string, after: string): DiffMark[] {
  if (before === after) return [{ type: "equal", text: after }];
  const a = tokenizeWords(before);
  const b = tokenizeWords(after);
  if (a.length + b.length > MAX_WORD_TOKENS) {
    return [
      ...(before !== null && before.length > 0
        ? [{ type: "delete" as const, text: before }]
        : []),
      ...(after !== null && after.length > 0
        ? [{ type: "insert" as const, text: after }]
        : []),
    ];
  }
  return diffTokens(a, b);
}

function splitParagraphs(text: string): string[] {
  if (text === null || text.length === 0) return [];
  return text.split(PARA_SPLIT_RE);
}

/**
 * Diff two prose strings into blocks suitable for an in-paragraph review UI.
 * Unchanged paragraphs stay whole; changed ones carry word-level marks.
 */
export function diffProse(beforeRaw: string, afterRaw: string): DiffBlock[] {
  const before = proseForHistory(beforeRaw);
  const after = proseForHistory(afterRaw);
  if (before === after) {
    return after !== null && after.length > 0
      ? [{ kind: "unchanged", text: after }]
      : [];
  }

  const a = splitParagraphs(before);
  const b = splitParagraphs(after);
  const n = a.length;
  const m = b.length;

  const dp: Uint16Array[] = Array.from(
    { length: n + 1 },
    () => new Uint16Array(m + 1),
  );
  for (let i = 1; i <= n; i++) {
    const row = dp[i]!;
    const prevRow = dp[i - 1]!;
    for (let j = 1; j <= m; j++) {
      row[j] =
        a[i - 1] === b[j - 1]
          ? (((prevRow[j - 1] ?? 0) + 1) as number)
          : Math.max(prevRow[j] ?? 0, row[j - 1] ?? 0);
    }
  }

  type ParaOp =
    | { type: "equal"; text: string }
    | { type: "delete"; text: string }
    | { type: "insert"; text: string };

  const ops: ParaOp[] = [];
  let i = n;
  let j = m;
  while (i > 0 || j > 0) {
    const aTokM = i > 0 ? atIndexMaybe(a, i - 1) : nothing;
    const bTokM = j > 0 ? atIndexMaybe(b, j - 1) : nothing;
    const rowM = atIndexMaybe(dp, i);
    const prevRowM = atIndexMaybe(dp, i - 1);
    if (
      i > 0 &&
      j > 0 &&
      isJust(aTokM) &&
      isJust(bTokM) &&
      aTokM.value === bTokM.value
    ) {
      ops.push({ type: "equal", text: aTokM.value });
      i--;
      j--;
    } else if (
      j > 0 &&
      isJust(bTokM) &&
      // Nothing left on the left (i === 0) means the rest is inserted; dp
      // has no row -1 to compare against.
      (i === 0 ||
        (isJust(rowM) &&
          isJust(prevRowM) &&
          (rowM.value[j - 1] ?? 0) >= (prevRowM.value[j] ?? 0)))
    ) {
      ops.push({ type: "insert", text: bTokM.value });
      j--;
    } else if (isJust(aTokM)) {
      ops.push({ type: "delete", text: aTokM.value });
      i--;
    } else {
      break;
    }
  }
  ops.reverse();

  const blocks: DiffBlock[] = [];
  for (let k = 0; k < ops.length; k++) {
    const op = ops[k]!;
    if (op.type === "equal") {
      blocks.push({ kind: "unchanged", text: op.text });
      continue;
    }
    // Pair adjacent delete+insert into an in-paragraph edit when possible.
    const next = ops[k + 1];
    if (op.type === "delete" && next?.type === "insert") {
      blocks.push({
        kind: "changed",
        marks: wordDiff(op.text, next.text),
      });
      k++;
      continue;
    }
    if (op.type === "insert") {
      blocks.push({
        kind: "changed",
        marks: [{ type: "insert", text: op.text }],
      });
    } else {
      blocks.push({
        kind: "changed",
        marks: [{ type: "delete", text: op.text }],
      });
    }
  }
  return blocks;
}

export function changeStats(blocks: DiffBlock[]): {
  added: number;
  removed: number;
} {
  let added = 0;
  let removed = 0;
  const countWords = (s: string): number =>
    (s.trim().match(/\S+/g) ?? []).length;
  for (const block of blocks) {
    if (block.kind === "unchanged") continue;
    for (const mark of block.marks) {
      if (mark.type === "insert") added += countWords(mark.text);
      if (mark.type === "delete") removed += countWords(mark.text);
    }
  }
  return { added, removed };
}

export type HistoryRange = {
  from: number;
  to: number;
  type: "insert" | "delete";
};

export type HistoryReviewDoc = {
  text: string;
  ranges: HistoryRange[];
};

/**
 * Flatten a prose diff into one editor document with absolute insert/delete
 * ranges — so the main editor can show the change in place.
 */
export function buildHistoryReview(
  beforeRaw: string,
  afterRaw: string,
): HistoryReviewDoc {
  const blocks = diffProse(beforeRaw, afterRaw);
  let text = "";
  const ranges: HistoryRange[] = [];

  for (let i = 0; i < blocks.length; i++) {
    if (i > 0) text += "\n\n";
    const block = blocks[i]!;
    if (block.kind === "unchanged") {
      text += block.text;
      continue;
    }
    for (const mark of block.marks) {
      const from = text.length;
      text += mark.text;
      const to = text.length;
      if ((mark.type === "insert" || mark.type === "delete") && from < to) {
        ranges.push({ from, to, type: mark.type });
      }
    }
  }

  return { text, ranges };
}
